import { createSlice } from "@reduxjs/toolkit";
import {
  addToCartBuilder,
  removeFromCartBuilder,
  clearCartBuilder,
  loadCartBuilder,
  applyPromoBuilder,
} from "./builder";
import { LOGOUT } from "../../../utils/constants";

const initialState = {
  items: [],
  totalItems: 0,
  totalAmount: 0,
  loading: false,
  error: null,
  appliedPromo: null,
};

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    // Synchronous actions
    setAppliedPromo: (state, { payload }) => {
      state.appliedPromo = payload;
    },
    clearAppliedPromo: (state) => {
      state.appliedPromo = null;
    },
    calculateTotals: (state) => {
      state.totalItems = state.items.length;
      state.totalAmount = state.items.reduce((sum, item) => sum + Number(item.price), 0);
    },
  },
  extraReducers: (builder) => {
    addToCartBuilder(builder);
    removeFromCartBuilder(builder);
    clearCartBuilder(builder);
    loadCartBuilder(builder);
    applyPromoBuilder(builder);

    builder.addCase(LOGOUT, () => ({ ...initialState }));
  },
});

export const { 
  setAppliedPromo, 
  clearAppliedPromo, 
  calculateTotals 
} = cartSlice.actions;

// Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectCartTotalItems = (state) => state.cart.totalItems;
export const selectCartTotalAmount = (state) => state.cart.totalAmount;
export const selectCartLoading = (state) => state.cart.loading;
export const selectCartError = (state) => state.cart.error;
export const selectAppliedPromo = (state) => state.cart.appliedPromo;

// Derived selectors
export const selectCartItemById = (state, courseId) => 
  state.cart.items.find(item => item.courseId === courseId);

export const selectCartIsEmpty = (state) => state.cart.items.length === 0;

export const selectCartSubtotal = (state) => state.cart.totalAmount;

export const selectCartDiscount = (state) => {
  if (!state.cart.appliedPromo) return 0;
  const { percentage, applicableCourseIds } = state.cart.appliedPromo;

  if (!applicableCourseIds || applicableCourseIds.length === 0) {
    return Math.round(state.cart.totalAmount * percentage);
  }

  const applicableTotal = state.cart.items
    .filter((item) => applicableCourseIds.includes(item.courseId))
    .reduce((sum, item) => sum + Number(item.price), 0);

  return Math.round(applicableTotal * percentage);
};

export const selectCartTotal = (state) => {
  const subtotal = state.cart.totalAmount;
  const discount = selectCartDiscount(state);
  return subtotal - discount;
};

export default cartSlice.reducer;